import { AppPathsMap } from '@core/routing/app-paths';

/**
 * Routes that are meant to be viewed without a session: the login page, the
 * logout confirmation and every error page. The app initializer must not
 * redirect away from these — doing so would drop their query string (e.g. the
 * `?email=` deep link carried by an invitation) and hide the reason the user
 * was sent there in the first place.
 */
const anonymousAccessiblePaths: readonly string[] = [
  AppPathsMap.Login,
  AppPathsMap.Logout,
  ...Object.values(AppPathsMap.Error),
];

/**
 * Whether the current location matches the given single-segment route path.
 * Matched with `endsWith` so a deployment under a base href still resolves.
 *
 * @param {string} path - The route path segment to test.
 * @returns {boolean} `true` when the current pathname ends with the given segment.
 */
const isOnPath = (path: string): boolean => {
  try {
    return globalThis.location.pathname.endsWith(`/${path}`);
  } catch {
    return false;
  }
};

/**
 * Determines whether the current page is one a visitor may see without a
 * session, and which therefore must keep its URL untouched during bootstrap.
 *
 * @returns {boolean} `true` if the current page is anonymously accessible; otherwise, `false`.
 */
export const isOnAnonymousAccessiblePage = (): boolean => anonymousAccessiblePaths.some((path) => isOnPath(path));
